import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { GroupSummary } from '../api';
import { newGroupPath } from '../routes';
import type { FetchState } from './MaterialList';
import { plural } from './plural';

/**
 * The groups this teacher is in, and the way to start one (R1).
 *
 * Three states, because all three are ordinary. A teacher opening Collab for the first time
 * has no groups — that is not an edge case, it is the first screen every account sees — and
 * a failed fetch is a flaky network rather than a fault worth a stack trace.
 *
 * **Starting one is a link now, to `/new`.** It used to be a bare name field sitting under the
 * list, which made the first screen of the product a text box with no context and left the
 * colleagues — the entire point of a group — to a second act inside a screen the teacher had
 * not seen yet. It is a destination now, for the same reason adding a material is
 * (`MaterialUploadPage`): a real address, a back button that works, one screen that does one
 * thing. What is left here is the list, with one primary control in the corner pointing at it.
 *
 * The control is offered twice — in the toolbar and in the empty state — exactly as
 * `MaterialList` offers "Add material" twice, and for the same reason: a teacher with no
 * groups has nothing else on this screen to do.
 *
 * **Except when someone has invited them.** The invitations banner (`PendingInvitations`) is
 * passed in as a slot rather than built here, so this stays presentational: `App` owns the
 * fetch and the navigation, as it does for the groups. When the teacher has no groups and the
 * banner has something in it, the empty state points at the banner and drops its New group —
 * a colleague's group waiting one press away is the better first act than starting an empty
 * one, and two primary controls would leave the teacher choosing between them (R9).
 */

export interface GroupListProps {
  groups: GroupSummary[];
  state: FetchState;
  /** Where this group's room lives. Built by `src/routes.ts`; never carries a token. */
  linkTo: (group: GroupSummary) => string;
  onRetry: () => void;
  /** The invitations banner, rendered above the groups. Renders nothing when there are none. */
  invitations?: ReactNode;
  /** True while the banner is showing at least one invitation. */
  hasInvitations?: boolean;
  /** False when the unread kill switch is on. Counts show by default (R11). */
  showUnread?: boolean;
}

/**
 * How many of a group's materials have unread comments (R7). Zero, or a Worker that sent no
 * count, draws nothing. The digit is for the eye; the sentence is what a screen reader hears.
 */
function UnreadCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="unread-count">
      <span aria-hidden="true">{count}</span>
      <span className="visually-hidden">
        {`${plural(count, 'material')} with unread comments`}
      </span>
    </span>
  );
}

export default function GroupList({
  groups, state, linkTo, onRetry, invitations = null, hasInvitations = false, showUnread = true,
}: GroupListProps) {
  return (
    <section className="group-library" aria-label="Your groups">
      <div className="toolbar">
        <div className="toolbar-title">
          <p className="library-eyebrow">A shared table</p>
          <h2>Your groups</h2>
          <p className="library-intro">Good teaching grows in good company. Share materials with the colleagues you choose.</p>
          {state === 'ready' && groups.length > 0 && (
            <p className="meta">
              {groups.length} {groups.length === 1 ? 'group' : 'groups'}
            </p>
          )}
        </div>
        {/* The corner. A link, because starting a group is somewhere a teacher goes. */}
        <div className="row-actions">
          <Link className="button-link primary-link" to={newGroupPath()}>New group</Link>
        </div>
      </div>

      {invitations}

      <section className="group-library-content" aria-label="Groups" aria-busy={state === 'loading'}>
        {state === 'loading' && <p className="hint" role="status">Loading your groups…</p>}

        {state === 'failed' && (
          <div role="alert">
            <p className="error">We could not load your groups.</p>
            <button type="button" onClick={onRetry}>Try again</button>
          </div>
        )}

        {state === 'ready' && groups.length === 0 && hasInvitations && (
          <div className="empty group-empty">
            <h3>A colleague has saved you a seat</h3>
            <p className="hint" role="status">
              You have an invitation above. Join it, or start a group of your own from the
              corner.
            </p>
          </div>
        )}

        {state === 'ready' && groups.length === 0 && !hasInvitations && (
          <div className="empty group-empty">
            <h3>Make room for your colleagues</h3>
            <p className="hint" role="status">
              You are not in a group yet. Start one and invite the colleagues whose opinion you
              want.
            </p>
            <Link className="button-link primary-link" to={newGroupPath()}>New group</Link>
          </div>
        )}

        {state === 'ready' && groups.length > 0 && (
          <ul className="group-cards">
            {groups.map((group) => (
              <li className="group-card" key={group.id}>
                {/* A link, so a group room has an address a teacher can keep. */}
                <span className="group-card-label">Shared group</span>
                <h3><Link className="collab-group-name" to={linkTo(group)}>{group.name}</Link></h3>
                <div className="group-card-footer">
                  <span className="meta">{group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}</span>
                  {showUnread && <UnreadCount count={group.unreadMaterials ?? 0} />}
                  <span className="group-role">{group.role}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
